import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import { DbKey } from '../../../../../adl-gen/common/db';
import {
  makeInternationalized,
  makeWorkRoleScoreRange,
  ScoreRange,
  WorkRole,
  WorkRoleScoreRange
} from '../../../../../adl-gen/totalinteraction/futurefit/db';
import { LangCode, withI18n, WithI18nProps } from '../../../../../internationalization/i18n-hoc';
import { PlusIcon } from '../../../../../ui/common/icons';
import { Button } from '../../../../../ui/widgets/button/button';
import {
  FieldState,
  FormValue,
  newIntegerFieldState,
  newNonEmptyTextField,
  withValueCallback
} from '../../../../../ui/widgets/text-input/text-field';
import { debounce } from '../../../../../util/timers';
import { uniqueId } from '../../../../../util/utils';
import { ScoreRangeUpdate } from '../../../../stores/work-role-store';
import { I18nInput, I18nTextInputObserver, TextInputObserver } from '../../../common/editor';
import { DnDContainer, DraggableEditorOption } from '../../drag-and-drop/drag-and-drop-container';

const styles = require('../../../common/editor.css');

interface ScoreRangeEditorProps extends WithI18nProps {
  debug?: boolean;
  lang?: LangCode;
  roleId: DbKey<WorkRole>;
  scoreRanges: ScoreRangeUpdate[];

  postUpdates(scoreRanges: ScoreRangeUpdate[]): void;

  onCancel(): void;
}

const defaultScoreRanges: ScoreRange[] = [
  {
    label: {
      kind: 'text',
      value: makeInternationalized({ en: 'Apprentice', fr: 'Apprenti/e', ja: '主務', ko: null, zh: '谙练' })
    },
    range: { v1: 0, v2: 33 }
  },
  {
    label: {
      kind: 'text',
      value: makeInternationalized({ en: 'Journeyman', fr: 'Compagnon', ja: 'アドバンスド', ko: null, zh: '先进' })
    },
    range: { v1: 34, v2: 66 }
  },
  {
    label: {
      kind: 'text',
      value: makeInternationalized({ en: 'Master', fr: 'Maître/sse', ja: '達人', ko: null, zh: '师傅' })
    },
    range: { v1: 67, v2: 99 }
  }
];

const minScore = 0;
const maxScore = 100;
const errorMsg = 'field must be an integer between {0} and {1}';

const format = (formatString: string, args: string[] | number[]): string => {
  return [...args].reduce((s: string, a, i) => s.replace('{' + i + '}', `${a}`), formatString);
};

type ScoreRangeInput =
  | {
      kind: 'new';
      id: number;
      roleId: string;
      label: I18nInput;
      min: FieldState<number>;
      max: FieldState<number>;
    }
  | {
      kind: 'existing' | 'deleted';
      id: number;
      dbId: string;
      roleId: string;
      label: I18nInput;
      min: FieldState<number>;
      max: FieldState<number>;
    };

/**
 * Base component to create/update score ranges.
 */
@observer
class ScoreRangeEditor0 extends React.Component<ScoreRangeEditorProps> {
  @observable scoreRangeList: ScoreRangeInput[] = [];

  lang: LangCode;

  constructor(props: Readonly<ScoreRangeEditorProps>) {
    super(props);
    this.lang = props.lang ? props.lang : props.currentLangCode();
    const { initI18nFromInput: initFromInput, scoreRanges } = props;
    if (scoreRanges.length > 0) {

      for (const sr of scoreRanges) {
        const label: I18nInput = makeInternationalized({
          en: withValueCallback(newNonEmptyTextField(), debounce(1000, this.onUpdateRange))
        });
        const min = withValueCallback(
          newIntegerFieldState(minScore, maxScore, format(errorMsg, [minScore, maxScore])),
          debounce(1000, this.onUpdateRange)
        );
        const max = withValueCallback(
          newIntegerFieldState(minScore, maxScore, format(errorMsg, [minScore, maxScore])),
          debounce(1000, this.onUpdateRange)
        );

        /// Standard situation with existing role
        if (sr.kind === 'update') {
          const id = sr.value.id;
          const value = sr.value.value;
          const roleId = value.workRoleId;

          initFromInput(label, value.scoreRange.label);
          min.onChange(`${value.scoreRange.range.v1}`);
          max.onChange(`${value.scoreRange.range.v2}`);

          this.scoreRangeList.push({ kind: 'existing', id: uniqueId(), dbId: id, roleId, label, min, max });

          /// Clone role (Save As) situation
        } else if (sr.kind === 'create') {
          const value = sr.value;
          const roleId = sr.value.workRoleId;

          initFromInput(label, value.scoreRange.label);
          min.onChange(`${value.scoreRange.range.v1}`);
          max.onChange(`${value.scoreRange.range.v2}`);

          this.scoreRangeList.push({ kind: 'new', id: uniqueId(), roleId, label, min, max });
        }
        /// OK now, sort for display
        this.scoreRangeList = this.scoreRangeList
          .slice()
          .sort((a, b) =>
            Number(a.max.value()) < Number(b.max.value()) ? -1 : Number(a.max.value()) > Number(b.max.value()) ? 1 : 0
          );
      }
    } else {
      /// Start with defaults

      for (const sr of defaultScoreRanges) {
        const label: I18nInput = makeInternationalized({
          en: withValueCallback(newNonEmptyTextField(), debounce(1000, this.onUpdateRange))
        });
        const min = withValueCallback(
          newIntegerFieldState(minScore, maxScore, format(errorMsg, [minScore, maxScore])),
          debounce(1000, this.onUpdateRange)
        );
        const max = withValueCallback(
          newIntegerFieldState(minScore, maxScore, format(errorMsg, [minScore, maxScore])),
          debounce(1000, this.onUpdateRange)
        );
        // initEmpty(label);
        initFromInput(label, sr.label);
        min.onChange(`${sr.range.v1}`);
        max.onChange(`${sr.range.v2}`);
        this.scoreRangeList.push({ kind: 'new', id: uniqueId(), roleId: this.props.roleId, label, min, max });
        /// OK now, sort for display
        this.scoreRangeList = this.scoreRangeList
          .slice()
          .sort((a, b) =>
            Number(a.max.value()) < Number(b.max.value()) ? -1 : Number(a.max.value()) > Number(b.max.value()) ? 1 : 0
          );
      }
    }
    this.onSave();
  }

  RangeObserver = observer(
    (props: { label: I18nInput; min: FieldState<number>; max: FieldState<number>; idx: number }) => {
      return (
        <React.Fragment>
          {I18nTextInputObserver({
            field: props.label,
            type: 'textarea',
            lang: this.lang,
            title: '', //`Range ${props.idx}`,
            placeholder: `Label text`,
            xsSpan: 4,
            textAreaRows: 1,
            textAreaCols: 50,
            debug: this.props.debug
          })}
          {TextInputObserver({
            field: props.min,
            type: 'number',
            title: 'Min',
            placeholder: `min ${minScore} max ${maxScore}`,
            xsSpan: 1,
            debug: this.props.debug
          })}
          {TextInputObserver({
            field: props.max,
            type: 'number',
            title: 'Max',
            placeholder: `min ${minScore} max ${maxScore}`,
            xsSpan: 1,
            debug: this.props.debug
          })}
        </React.Fragment>
      );
    }
  );

  @computed
  get inputIsValid(): boolean {
    return (
      this.currentMin >= minScore &&
      this.currentMax <= maxScore &&
      this.scoreRangeList.every(v => {
        return (
          v.min.getValid() !== undefined &&
          v.max.getValid() !== undefined &&
          Number(v.min.value()) <= Number(v.max.value()) &&
          Number(v.max.value()) > 0
        );
      })
    );
  }

  @action
  onSave = () => {
    const { makeI18nContent, postUpdates } = this.props;
    const updatedRanges: ScoreRangeUpdate[] = [];

    /// rationalize seqs CURRENTLY A NO-OP
    /// This may change seqs (eg. [1,3,4] -> [0,1,2])
    this.rebuildList();
    if (this.inputIsValid) {
      for (const v of this.scoreRangeList) {
        // if (v.min.getValid() !== undefined && v.max.getValid() !== undefined) {
        const range: WorkRoleScoreRange = makeWorkRoleScoreRange({
          workRoleId: v.roleId,
          scoreRange: {
            label: makeI18nContent(v.label),
            range: { v1: Number(v.min.value()), v2: Number(v.max.value()) }
          }
        });
        switch (v.kind) {
          case 'new':
            updatedRanges.push({ kind: 'create', value: range });
            break;
          case 'existing':
            updatedRanges.push({ kind: 'update', value: { id: v.dbId, value: range } });
            break;
          default:
            updatedRanges.push({ kind: 'delete', value: v.dbId });
        }
        // }
      }
      // Think we do actually want to post empty ranges... But we need data integrity links between WorkRoleScoreRange and Competency/SkillTrainingOffering
      if (updatedRanges.length > 0) {
        postUpdates(updatedRanges);
      }
    }
  };

  @computed
  get currentMax() {
    return this.scoreRangeList
      .filter(r => r.kind !== 'deleted')
      .reduce((acc, next) => {
        if (next.min.getValid() !== undefined && next.max.getValid() !== undefined) {
          const min = Number(next.min.value());
          const max = Number(next.max.value());
          let retval = acc >= min ? acc : min;
          retval = acc >= max ? acc : max;
          return retval;
        }
        return acc;
      }, minScore);
  }

  @computed
  get currentMin() {
    return this.scoreRangeList
      .filter(r => r.kind !== 'deleted')
      .reduce((acc, next) => {
        if (next.min.getValid() !== undefined && next.max.getValid() !== undefined) {
          const min = Number(next.min.value());
          const max = Number(next.max.value());
          let retval = acc < max ? acc : max;
          retval = acc < min ? acc : min;
          return retval;
        }
        return acc;
      }, maxScore);
  }

  /// Reorganize to set contiguous seqs
  /// CURRENTLY A NO-OP
  @action
  rebuildList = () => {
  };

  @action
  onAddRange = () => {
    const { initI18nEmpty: initEmpty, roleId } = this.props;
    /// First make sure we can add a range - add all the existing ranges and
    /// ensure they are less than 100
    if (this.currentMin >= minScore && this.currentMax < maxScore) {
      const label: I18nInput = makeInternationalized({
        en: withValueCallback(newNonEmptyTextField(), debounce(1000, this.onUpdateRange))
      });

      const newMinScore = this.currentMax + 1;
      const min = withValueCallback(newIntegerFieldState(minScore, maxScore), debounce(1000, this.onUpdateRange));
      const max = withValueCallback(newIntegerFieldState(newMinScore, maxScore), debounce(1000, this.onUpdateRange));
      initEmpty(label);
      /// Default to all renaining space - and ignore space at the bottom of the range
      min.onChange(`${newMinScore}`);
      max.onChange(`${maxScore}`);

      this.scoreRangeList.push({ kind: 'new', id: uniqueId(), label, min, max, roleId });
    }
    this.onSave();
  };

  /// NB 'key' would be array index if DnD (draggable); otherwise a unique ID
  @action
  onRemoveRange = (key: number) => {
    const toBeDeleted = this.scoreRangeList.findIndex(sr => sr.id === key);
    if (toBeDeleted >= 0) {
      if (this.scoreRangeList[toBeDeleted].kind === 'new') {
        /// Just delete it
        this.scoreRangeList.splice(toBeDeleted);
      } else if (this.scoreRangeList[toBeDeleted].kind === 'existing') {
        /// Mark it as "deleted" so it can be deleted on the server
        this.scoreRangeList[toBeDeleted].kind = 'deleted';
      }
    }
    this.onSave();
  };

  @action
  onReorder = (newOrder: number[]) => {
    const newList: ScoreRangeInput[] = [];

    newOrder.forEach(key => {
      const record = this.scoreRangeList.find(rec => rec.id === key);
      if (record) {
        newList.push(record);
      }
    });

    this.scoreRangeList = newList;
    this.onSave();
  };

  @action
  onUpdateRange = (_range: FormValue<number | string>) => {
    this.onSave();
  };

  AddButton = () => {
    return (
      <div className={styles.buttonListContainer}>
        <div className={styles.buttonList}>
          <Button
            onClick={async () => this.onAddRange()}
            Icon={PlusIcon}
            size="small"
            variant="round"
            disabled={this.currentMax >= maxScore}
          >
            Add
          </Button>
        </div>
      </div>
    );
  };

  OrderedRanges = observer(
    (): JSX.Element => {
      const draggableDisplayOptions: DraggableEditorOption[] = [];
      for (const sr of this.scoreRangeList.filter(range => range.kind !== 'deleted')) {
        let i = 0;
        draggableDisplayOptions.push({
          id: sr.id,
          /// TODO: If reverting to DnD (draggable), idx must be the array index not the unique ID
          element: <this.RangeObserver label={sr.label} min={sr.min} max={sr.max} idx={i} />,
          control: (
            <Button size="small" variant="round" children="Delete" onClick={async () => this.onRemoveRange(sr.id)} />
          )
        });
        i += 1;
      }
      return (
        <DnDContainer
          draggableOptions={draggableDisplayOptions}
          onMove={opt => this.onReorder(opt.map(item => item.id))}
        />
      );
    }
  );

  Edit = () => {
    return (
      <div className={styles.page}>
        <div className={styles.body}>
          <this.OrderedRanges />
        </div>
      </div>
    );
  };

  render() {
    const title = `Score Ranges and Labels`;
    return (
      <React.Fragment>
        <h4>{title}</h4>
        <this.AddButton />
        <this.Edit />
      </React.Fragment>
    );
  }
}

export const ScoreRangeEditor = withI18n(ScoreRangeEditor0)



// WEBPACK FOOTER //
// ./src/admin/ui/components/editors/workrole-editor/score-range-editor.tsx